import React, { useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import Badge from '../../../components/ui/Badge';
import { ReferenceDataGeneralList, type ReferenceInboxColumn } from '../ReferenceDataGeneralList';
import CertCatalogWorkspace from './CertCatalogWorkspace';
import { CrewLookupKindContext, useCrewLookupKind } from './CrewLookupKindContext';
import { buildCrewLookupModuleConfig, type CrewLookupRouteOptions } from './config';
import { getCrewLookupDef, type CrewLookupDef } from './registry';
import { useCrewLookupsList, type CrewLookupItem } from './useData';
import type { ListQueryOptions } from '../../../services/paginatedList';

function useCrewLookupListData(
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) {
  return useCrewLookupsList(useCrewLookupKind(), searchParams, page, limit, options);
}

function buildColumns(def: CrewLookupDef, formatDate: (s?: string) => string): ReferenceInboxColumn<CrewLookupItem>[] {
  const cols: ReferenceInboxColumn<CrewLookupItem>[] = [
    {
      key: 'code',
      header: 'Code',
      className: 'whitespace-nowrap',
      render: (row) => (
        <span className="font-medium font-mono text-xs tracking-wide">{row.code || ''}</span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (row) => row.name,
    },
  ];
  if (def.extras.includes('abrv')) {
    cols.push({
      key: 'abrv',
      header: 'Abrv',
      className: 'hidden whitespace-nowrap sm:table-cell',
      render: (row) => <span className="font-mono text-xs">{row.abrv || ''}</span>,
    });
  }
  if (def.kind === 'ranks') {
    cols.push({
      key: 'groupType',
      header: 'Group',
      className: 'hidden whitespace-nowrap md:table-cell',
      render: (row) =>
        row.groupType === 1 ? 'Officers' : row.groupType === 2 ? 'Ratings' : '',
    });
  }
  if (def.extras.includes('phone')) {
    cols.push({
      key: 'phone',
      header: 'Phone',
      className: 'hidden whitespace-nowrap lg:table-cell',
      render: (row) => row.phone || '',
    });
  }
  if (def.extras.includes('email')) {
    cols.push({
      key: 'email',
      header: 'Email',
      className: 'hidden whitespace-nowrap xl:table-cell',
      render: (row) => row.email || '',
    });
  }
  cols.push(
    {
      key: 'isActive',
      header: 'Active',
      className: 'whitespace-nowrap',
      render: (row) => (
        <Badge variant={row.isActive ? 'success' : 'danger'}>{row.isActive ? 'Yes' : 'No'}</Badge>
      ),
    },
    {
      key: 'sortOrder',
      header: 'Order',
      className: 'hidden whitespace-nowrap md:table-cell',
      render: (row) => row.sortOrder ?? 0,
    },
    {
      key: 'createdAt',
      header: 'Created',
      className: 'hidden whitespace-nowrap lg:table-cell',
      render: (row) => formatDate(row.createdAt),
    },
  );
  return cols;
}

const ListInner: React.FC<{ def: CrewLookupDef; routes?: CrewLookupRouteOptions }> = ({ def, routes }) => {
  const moduleConfig = useMemo(() => buildCrewLookupModuleConfig(def, routes), [def, routes]);
  const columns = useMemo(
    () => buildColumns(def, moduleConfig.utils.formatDate),
    [def, moduleConfig.utils.formatDate],
  );

  return (
    <CrewLookupKindContext.Provider value={def.kind}>
      <ReferenceDataGeneralList
        moduleConfig={moduleConfig}
        useData={useCrewLookupListData as any}
        columns={columns}
        defaultSort={{ key: 'sortOrder', direction: 'asc' }}
        newLabel={`New ${def.singular.toLowerCase()}`}
        getRowLabel={(row) => row.name}
      />
    </CrewLookupKindContext.Provider>
  );
};

/** Embeddable panel for the combined crew reference workspace. */
export const CrewLookupListPanel: React.FC<{ kind: string; routes?: CrewLookupRouteOptions }> = ({
  kind,
  routes,
}) => {
  const def = getCrewLookupDef(kind);
  if (!def) return null;
  if (def.kind === 'cert-catalogs') return <CertCatalogWorkspace />;
  return <ListInner def={def} routes={routes} />;
};

const List: React.FC = () => {
  const { kind } = useParams<{ kind: string }>();
  const def = getCrewLookupDef(kind);
  if (!def) return <Navigate to="/parameters/crew" replace />;
  return <Navigate to={`/parameters/crew?section=${def.kind}`} replace />;
};

export default List;
